import {
  asyncOperation,
  clientConfig,
  clientRepo,
  getDownloadUrl,
  toastStore,
  useDeepDeps,
  useUser,
} from "@zickko/client";
import { EmailKey, GcsFile, gcsPaths, idFactory, sharedConfig } from "@zickko/shared";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import { useCallback, useEffect, useState } from "react";
import { useOrg } from "../../providers/OrgProvider";
import toast from "react-hot-toast";
import { OutboundAttachedFiles, OutboundEmail } from "@zickko/shared/src/models/OutboundEmail";
import { writeStringToGcs } from "@zickko/client/src/gcs/writeStringToGcs";
import { Status } from "@zickko/shared/src/models/Status";
import { writeFileToGcs } from "@zickko/client/src/gcs/writeFileToGcs";

type Props = {
  email: OutboundEmail;
};
export const OutboundEmailViewWidget = (props: Props) => {
  const { email } = props;

  const { org } = useOrg();
  const { user } = useUser();

  const [htmlContentUrl, setHtmlContentUrl] = useState<string | null>(null);
  const [formattedDate, setFormattedDate] = useState<string | null>(null);
  const [htmlContent, setHtmlContent] = useState(null);
  const [replyText, setReplyText] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [busyFlag, setBusyFlag] = useState(false);

  const getHtmlContentUrl = useCallback(() => {
    const storage = getStorage();
    const pathReference = ref(
      storage,
      gcsPaths.outboundEmailHtmlContentPath({
        orgId: org.id,
        emailId: email?.id,
        uid: user.uid,
      })
    );
    if (email !== undefined && email !== null) {
      getDownloadURL(pathReference)
        .then(async (url) => {
          setHtmlContentUrl(url);
          const response = await fetch(url);
          const text = await response.text();
          setHtmlContent(text);
          setHtmlContentUrl(url);
        })
        .catch((e) => {
          toastStore.error("Error loading email content.");
        });
    }
  }, [email]);

  const formatDate = (inputDateStr) => {
    const date = new Date(inputDateStr);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    };
    setFormattedDate(date.toLocaleString("en-US", options));
  };

  useEffect(() => {
    getHtmlContentUrl();
    formatDate(email?.createdIsoDateTimeUtc);
  }, [getHtmlContentUrl]);

  if (email === undefined) {
    return <div>Loading...</div>;
  }

  if (email === null) {
    return <div>Email not found.</div>;
  }

  const onDownloadAttachment = (attachment: OutboundAttachedFiles) => {
    const storage = getStorage();
    const pathReference = ref(storage, attachment.gcsFile.path);

    asyncOperation({
      fn: async () => {
        const url = await getDownloadURL(pathReference); // Get the download URL
        const response = await fetch(url); // Fetch the file as a Blob
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob); // Create a Blob URL for the download
        return { blobUrl, filename: attachment.gcsFile.filename || "file.pdf" };
      },
      onSuccess: ({ blobUrl, filename }) => {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", filename);
        document.body.appendChild(link); // Append the link to the DOM, trigger the download, and clean up
        link.style.display = "none";
        link.click(); // Trigger the download
        document.body.removeChild(link); // Remove the link from DOM
        window.URL.revokeObjectURL(blobUrl); // Release the blob URL
      },
      onError: (error) => {
        console.error("Error downloading attachment:", error);
        toastStore.error("Error downloading attachment.");
      },
      loadingMessage: "Downloading attachment...",
    });
  };

  const renderAttachments = () => {
    if (!email.attachedFiles || email.attachedFiles.length === 0) {
      return null;
    }
    return (
      <div>
        <h2>Attachments</h2>
        <div className="attachments-div">
          {email.attachedFiles
            .filter((e) => e.contentDisposition === "attachment")
            .map((attachment: OutboundAttachedFiles, index: number) => {
              return (
                <div key={attachment.gcsFile.id} style={{ textAlign: "center" }}>
                  <div onClick={() => onDownloadAttachment(attachment)}>
                    <span className="email-attachment-text">{`Attachment ${index + 1}`}</span>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  const renderHtmlContent = () => {
    if (!htmlContent) {
      return null;
    }
    return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  };

  const uploadHtmlContent = async (outboundEmailId: string) => {
    const bucketName = clientConfig.storageBucket;
    const emailKey: EmailKey = { orgId: org.id, uid: user.uid, emailId: outboundEmailId };
    const folderPath = gcsPaths.outboundEmailHtmlContentPath({ ...emailKey });

    const result = await asyncOperation({
      fn: () =>
        writeStringToGcs({
          contents: replyText,
          gcsBucket: bucketName,
          gcsFolder: folderPath,
          fileExtension: "html",
          contentType: "html",
          filename: "html_content",
        }),
      loadingMessage: `Uploading file`,
    });

    return result;
  };

  const uploadAttachments = async (outboundEmailId: string): Promise<OutboundAttachedFiles[]> => {
    const bucketName = clientConfig.storageBucket;
    const uploadAttachmentMetadata: OutboundAttachedFiles[] = [];

    await asyncOperation({
      loadingMessage: "Uploading attachments...",
      successMessage: "Attachments uploaded successfully!",
      fn: async () => {
        await Promise.all(
          attachments.map(async (file) => {
            const path = gcsPaths.outboundAttachmentPath({
              orgId: org.id,
              uid: user.uid,
              emailId: outboundEmailId,
              attachmentId: idFactory.createId(),
            });

            // Upload file to GCS
            await writeFileToGcs({
              file,
              path,
              bucket: bucketName,
              onProgress: (percentage) => {
                setUploadProgress((prev) => ({
                  ...prev,
                  [file.name]: percentage,
                }));
              },
            });

            // Collect file metadata
            const metadata: OutboundAttachedFiles = {
              gcsFile: {
                path: path,
                id: idFactory.createId(),
                bucket: bucketName,
                filename: file.name || "",
              },
              contentType: file.type,
              contentDisposition: "attachment",
              size: file.size,
            };

            uploadAttachmentMetadata.push(metadata);
          })
        );
      },
      onError: (e) => console.error("Failed to upload attachments:", e),
      onComplete: () => console.log("Upload process completed."),
    });

    return uploadAttachmentMetadata;
  };

  const createOutboundEmailEntity = async (
    outboundEmailId: string,
    htmlContent: GcsFile,
    attachments?: OutboundAttachedFiles[]
  ) => {
    const outboundEmail: OutboundEmail = {
      id: outboundEmailId,
      uid: user.uid,
      orgId: org.id,
      from: user.email,
      to: email.from,
      subject: email.subject,
      status: Status.failure,
      htmlContent: htmlContent,
      attachedFiles: attachments || [],
    };

    await asyncOperation({
      fn: () =>
        clientRepo.outboundEmail.set({ uid: user.uid, orgId: org.id, emailId: outboundEmail.id }, outboundEmail),
      onSuccess: () => {
        toast.success("Reply sent successfully!");
      },
    });
  };

  const onSendReply = async () => {
    const outboundEmailId = idFactory.createId();

    await asyncOperation({
      fn: async () => {
        const htmlContent = await uploadHtmlContent(outboundEmailId); // Upload HTML content
        const outboundAttachmentMetadata = await uploadAttachments(outboundEmailId); // Upload attachments
        await createOutboundEmailEntity(outboundEmailId, htmlContent, outboundAttachmentMetadata); // Create outbound email entry
      },
      loadingMessage: "Sending reply...",
      setBusyFlag,
      onSuccess: () => {
        toast.success("Reply sent successfully!");
      },
      onError: (error) => {
        console.error("Error sending reply:", error);
        toast.error("Error sending reply.");
      },
    });
  };

  return (
    <>
      <div className="mail-header-container">
        <div className="name-timestamp-container">
          <span>{email.from}</span>
          <div className="timestamp">{formattedDate}</div>
        </div>
        <div className="subject">{email.subject}</div>
        <div className="sender">
          <span>Reply To:</span> {email.to}
        </div>
      </div>
      <div className="mail-content">{renderHtmlContent()}</div>
      {renderAttachments()}
      <div className="reply-section">
        <textarea
          placeholder="Reply..."
          className="text-area"
          onChange={(e) => setReplyText(e.target.value)}
        ></textarea>
        <div className="send-button-container">
          <div className="upload-attachments">
            <input
              type="file"
              multiple
              onChange={(e) => setAttachments((prev) => [...prev, ...Array.from(e.target.files || [])])}
            />
          </div>

          <div className="attachments-progress">
            {attachments.map((file) => (
              <div key={file.name}>
                {file.name}: {uploadProgress[file.name]?.toFixed(2) || 0}% uploaded
              </div>
            ))}
          </div>
          <button className="send-button" onClick={onSendReply}>
            Send
          </button>
        </div>
      </div>
    </>
  );
};
